import {
  useSuspenseQuery,
  type UseSuspenseQueryResult,
} from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";
import { type ChatRecord } from "@/shared/domain/chat";

export interface UseAgentSessionParams {
  /**
   * Identifier of the agent session to fetch. Required — the caller
   * must narrow ``sessionId`` to a string (e.g. by branching on the
   * value from ``useParams``) before invoking this hook.
   */
  sessionId: string;
}

/**
 * Fetches the full record for one agent session.
 *
 * Suspends until the response is available and throws to the nearest
 * ``<ErrorBoundary>`` on failure, so callers can keep the rendering of
 * the loaded state free of ``isLoading`` / ``error`` branching. Unlike
 * :func:`useChat`, this hook does not poll: the polling lives inside
 * the interactive ``<Chat />`` rendered for sessions the user owns,
 * and read-only sessions don't change after load.
 *
 * The query key (``["agentSession", sessionId]``) is distinct from the
 * sidebar's ``useChat`` key so a detail page mounted on the same screen
 * as the sidebar does not contend with it on cache invalidation.
 */
export function useAgentSession({
  sessionId,
}: UseAgentSessionParams): UseSuspenseQueryResult<ChatRecord, Error> {
  const { chatService } = useDomainServices();
  return useSuspenseQuery<ChatRecord, Error>({
    queryKey: ["agentSession", sessionId],
    queryFn: ({ signal }) =>
      chatService.getChatById(sessionId, true, { abortSignal: signal }),
  });
}
